import React, { ComponentProps } from "react";

import { cva } from "class-variance-authority";

import { cn } from "@/common/utils/css.utils";

type HeadingTags = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
type HeadingBodyTags = "p" | "span";

export type HeadingProps = ComponentProps<HeadingTags> & {
  as: HeadingTags | HeadingBodyTags;
  size?: HeadingTags;
};

const HeadingStyles = cva("font-title font-bold text-black", {
  variants: {
    size: {
      /** Desktop: 48px/48px, Mobile: 36px/40px */
      h1: "text-[2.25rem] leading-[2.5rem] md:text-[3rem] md:leading-[3rem]",
      /** Desktop: 32px/38px, Mobile: 30px/36px */
      h2: "text-[1.875rem] leading-[2.25rem] md:text-[1.875rem] md:text-[2rem] md:leading-[2.375rem]",
      /** Desktop: 24px/32px, Mobile: 24px/32px */
      h3: "text-[1.5rem] leading-[2rem] md:text-[1.5rem] md:leading-[2rem]",
      /** Desktop: 18px/28px, Mobile: 18px/28px */
      h4: "text-[1.125rem] leading-[1.75rem] md:text-[1.125rem] md:leading-[1.75rem]",
      /** Desktop: 16px/24px, Mobile: 16px/24px */
      h5: "text-[1rem] leading-[1.5rem] md:text-[1rem] md:leading-[1.5rem]",
      /** Desktop: 16px/24px, Mobile: 16px/24px */
      h6: "text-[1rem] leading-[1.5rem] md:text-[1rem] md:leading-[1.5rem]",
    },
  },
});

/**
 * Simple heading component.
 *
 * @param as Render heading as `<h1>`, `<h2>`, `<h3>`, `<h4>`, `<h5>`, `<h6>`, `<p>` or `<span>`.
 * @param size Heading style to apply.
 * @param className List of CSS classes to pass to the heading, p or span tag.
 * @param props Rest of the heading props.
 */
export const Heading = ({
  as,
  size = as === "p" || as === "span" ? "h6" : as,
  className,
  ...props
}: HeadingProps) => {
  const Component = as;

  return (
    <Component className={cn(HeadingStyles({ size }), className)} {...props} />
  );
};
